import { Schema } from 'effect';
import { SiteId } from './common.js';
/** DTO schemas for the /lights group. See http/code.ts for conventions. */
/**
 * The schedule a controller runs, as sent to the device.
 *
 * Kept deliberately terse — the Shelly script has a 16 KB HTTP response limit
 * and a small, undocumented script memory. Windows are `[from, to]` tuples in
 * epoch seconds UTC so the device never parses a date or reasons about a
 * timezone. See docs/plans/light_control.md §7.3.
 */
export const ScheduleResponse = Schema.Struct({
    /** Payload version, so the device can refuse a format it does not know. */
    v: Schema.Number,
    /** Server time, epoch seconds. Lets the device detect clock skew. */
    now: Schema.Number,
    /** Seconds until the device should poll again. */
    ttl: Schema.Number,
    /** After this many seconds without a poll, stop trusting the schedule. */
    staleAfter: Schema.Number,
    /**
     * One entry per channel, `c` = channel index, `w` = ordered [from, to]
     * windows.
     *
     * An array rather than a channel-keyed object because the Shelly mJS runtime
     * has no `Object.keys`, so a map would force the device into fragile
     * string-indexed probing. Arrays iterate with a plain `for`.
     */
    ch: Schema.Array(Schema.Struct({
        c: Schema.Number,
        w: Schema.Array(Schema.Tuple([Schema.Number, Schema.Number]))
    }))
});
export const PairDeviceParams = Schema.Struct({
    siteId: SiteId
});
export const PairDeviceBody = Schema.Struct({
    /** The controller this device drives. Must already exist in config/lights. */
    controllerId: Schema.String
});
/**
 * The token is returned in full exactly once, here. Only its hash is stored, so
 * it cannot be recovered afterwards — the admin UI must show it and tell the
 * installer to paste it into the device before navigating away.
 */
export const PairDeviceResponse = Schema.Struct({
    deviceId: Schema.String,
    token: Schema.String
});
export const PreviewParams = Schema.Struct({
    siteId: SiteId,
    controllerId: Schema.String
});
/**
 * Admin-facing view of the same projection the device receives, with the
 * diagnostics the wire format deliberately omits. Lets an admin confirm what
 * Velpr *would* switch before any hardware exists.
 */
export const PreviewResponse = Schema.Struct({
    now: Schema.Number,
    from: Schema.Number,
    to: Schema.Number,
    zones: Schema.Array(Schema.Struct({
        zoneId: Schema.String,
        channel: Schema.Number,
        /** Non-zero means a window hit maxOnMinutes — surface it, don't hide it. */
        truncated: Schema.Number,
        windows: Schema.Array(Schema.Tuple([Schema.Number, Schema.Number]))
    }))
});
export const ListDevicesParams = Schema.Struct({
    siteId: SiteId
});
/**
 * Device credentials for the admin device list. Deliberately omits the secret
 * hash — the client never needs it and it must not leave the server.
 */
export const ListDevicesResponse = Schema.Struct({
    devices: Schema.Array(Schema.Struct({
        deviceId: Schema.String,
        controllerId: Schema.String,
        createdAt: Schema.String,
        /** Non-empty ISO timestamp means the credential has been retired. */
        revokedAt: Schema.String,
        lastSeenAt: Schema.String
    }))
});
export const RevokeDeviceParams = Schema.Struct({
    siteId: SiteId,
    deviceId: Schema.String
});
export const RevokeDeviceResponse = Schema.Struct({
    revoked: Schema.Boolean
});
export const RevokeControllerDevicesParams = Schema.Struct({
    siteId: SiteId,
    controllerId: Schema.String
});
/** How many live keys were revoked. Sent when a controller is deleted (D76). */
export const RevokeControllerDevicesResponse = Schema.Struct({
    revoked: Schema.Number
});
export const ManualOverrideParams = Schema.Struct({
    siteId: SiteId,
    zoneId: Schema.String
});
/**
 * Hold the zone on for this many hours from now; 0 cancels. The server caps it
 * at MAX_MANUAL_ON_HOURS (D77).
 */
export const ManualOverrideBody = Schema.Struct({
    hours: Schema.Number
});
export const ManualOverrideResponse = Schema.Struct({
    /** The stored expiry, ISO; '' when cancelled. */
    manualOnUntil: Schema.String
});
//# sourceMappingURL=lights.js.map